import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { $currentUser, $isAuthenticated } from '~/lib/stores/auth';
import { formatRelativeTime } from '~/lib/time';
import { apiGetHandedOff, type HandedOffItem } from '~/lib/api/claims';

/*
 * Admin-only sidebar trace for handed-off generations (DEV-166 follow-up): a
 * claimed chat leaves the admin's list the moment it transfers — without this
 * it simply vanishes, which reads as data loss. Shows what went, to whom, and
 * when. Read-only on purpose: the chat isn't theirs anymore (opening it would
 * 403), so the rows deliberately aren't links.
 *
 * Renders nothing for non-admins and for admins with no claimed handoffs.
 */
export function HandedOffSection() {
  const navigate = useNavigate();
  const user = useStore($currentUser);
  const isAuthenticated = useStore($isAuthenticated);
  const isAdmin = isAuthenticated && user?.role === 'admin';

  const [items, setItems] = useState<HandedOffItem[]>([]);

  useEffect(() => {
    if (!isAdmin) {
      setItems([]);
      return;
    }

    apiGetHandedOff()
      .then(setItems)
      .catch(() => setItems([]));
  }, [isAdmin]);

  if (!isAdmin || items.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 border-t border-hairline pt-3">
      <div className="flex items-center gap-1.5 px-3 pb-1.5">
        <span className="i-ph:hand-coins text-[12px] text-muted" />
        <span className="ui-caption">Handed off</span>
      </div>

      {items.map((item) => (
        <button
          key={item.chatId}
          type="button"
          onClick={() => navigate(`/handed-off/${item.chatId}`)}
          className="block w-full cursor-pointer rounded-lg border-0 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-white/[0.04]"
          title="Open the read-only archive"
        >
          <div className="truncate text-[12.5px] text-secondary">{item.description || item.chatId}</div>
          <div className="truncate text-[11px] text-muted">
            Claimed by <span className="text-secondary">{item.claimedBy || 'a user'}</span>{' '}
            {formatRelativeTime(item.claimedAt)}
          </div>
        </button>
      ))}
    </div>
  );
}
